/* What the ticker says.
 *
 * The bar used to scroll three mottos and nothing else. It now scrolls the same
 * three mottos with four of Milo's own numbers interleaved between them, and the
 * whole design problem is the second half of that sentence: the ticker is on
 * screen on every tab, from the first second of a brand-new account, and it has
 * no error state, no empty state and no room for one. So every stat has to have
 * an honest answer for "there is no data behind me yet", and that answer is
 * always the same — say nothing, and let the mottos close the gap.
 *
 * Two rules this file exists to obey:
 *
 * 1. **Nothing is recomputed here.** The weight figures are `GET /trends`'
 *    `current_rolling_7d` and `delta_7d`, computed by `src/milo/queries.py` and
 *    served byte-identically to the MCP `get_trends` tool. A weekly rate worked
 *    out in the browser would drift from the RATE / WK tile on TRENDS and from
 *    whatever the coach quotes. Today's macros go through `dayTotals` in
 *    `screens/Macros.tsx` — imported, not reimplemented, because a third copy of
 *    the `daily_total` rule is a third chance to get it wrong.
 * 2. **The browser's clock never decides a day.** Which day "today" is comes from
 *    `serverToday`, which names it out of the data; see the reads in `useTicker`.
 *    When the data cannot name it, the ticker says nothing about today rather
 *    than guessing.
 *
 * Every number below is also on screen, in words, on TRENDS or MACROS — which is
 * what keeps `Ticker`'s `aria-hidden` honest. Adding a figure here that appears
 * nowhere else would make this bar the only place a fact is stated, and the
 * attribute would have to be revisited.
 */
import { useEffect, useMemo, useState } from 'react'

import { getNutrition, getTrends, type WeightTrend } from '../api'
import { groupByDate, serverToday } from '../screens/Macros'

/* ── Copy ────────────────────────────────────────────────────────────────── */

/**
 * The three phrases the bar has always carried, in their original order and
 * their original casing — `.milo-ticker-run` sets `text-transform: uppercase`,
 * so this is what reaches the screen as CARRY THE CALF DAILY. Byte-identical to
 * the placeholder array this module replaces, which is what makes the
 * everything-failed fallback provably the old behaviour rather than a lookalike.
 */
export const MOTTOS = [
  'Carry the calf daily',
  'Manual beats synced',
  'Keep the chain alive',
] as const

/* ── Input ───────────────────────────────────────────────────────────────── */

/** Today's totals, already through the `daily_total` rule. */
export interface TodayMacros {
  calories: number
  protein_g: number
}

/**
 * Everything the bar can know. `null` means "not loaded, or the read failed" —
 * the ticker draws no distinction between the two, because it has no way to
 * show one and the screen that owns the number reports its own failure.
 */
export interface TickerData {
  /** `GET /trends`' weight section. */
  weight: WeightTrend | null
  /** Today's macros, or null when no row is dated the server's today. */
  today: TodayMacros | null
}

const NO_DATA: TickerData = { weight: null, today: null }

/* ── Formatting ──────────────────────────────────────────────────────────── */

/**
 * A number the wire promised but did not necessarily deliver.
 *
 * The API types say `number | null`, and the screens trust that. This one file
 * does not: a truncated response, a proxy serving a stale error body or a
 * backend regression all arrive here as something that is not a number, and the
 * ticker is the single surface with nowhere to put the resulting `NaN`. Treating
 * it as absent costs one item and loses nothing a user could have read anyway.
 */
function finite(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

/**
 * A signed delta: `−1.1`, `+0.4`, and an unmoved week as a bare `0.0`.
 *
 * The sign is U+2212 MINUS, matching every other number in the app. Exact zero
 * is deliberately unsigned: `value > 0 ? '+' : '−'` prints `−0.0`, which reads as
 * a loss that did not happen, and `delta_7d` really does come back as 0.0 for a
 * week that held still. Same rule as `delta` in `screens/Trends.tsx`.
 */
function signed(value: number): string {
  if (value === 0) return '0.0'
  return (value > 0 ? '+' : '−') + Math.abs(value).toFixed(1)
}

/** Thousands grouped explicitly in en-US, so the bar reads 1,420 wherever the
 *  browser thinks it is. The design's own copy groups them — "OF 1,700". */
function grouped(value: number): string {
  return Math.round(value).toLocaleString('en-US')
}

/* ── The bar ─────────────────────────────────────────────────────────────── */

/**
 * The phrases to scroll, in order: a motto, the weight pair, a motto, the macro
 * pair, a motto.
 *
 * Every stat is independently droppable and the mottos are not, so the list
 * shrinks towards the three of them and never past. That matters more than it
 * looks: `Ticker` renders `null` for an empty list, which removes the yellow bar
 * from the sticky header entirely and jumps the tabs upward — an empty ticker is
 * a layout change, not a quiet one.
 *
 * Pure, and exported for the tests that pin each of those drops.
 */
export function buildTicker(data: TickerData): string[] {
  const [first, second, third] = MOTTOS

  const items: string[] = [first]

  const rolling = finite(data.weight?.current_rolling_7d)
  if (rolling !== null) items.push(`7-day weight ${rolling.toFixed(1)} lb`)

  // Null here is the server saying the rate is not computable — a single
  // weigh-in, or a window that reaches back no further than the last one. A rate
  // of 0.0 in that case would be a claim, not a measurement.
  const rate = finite(data.weight?.delta_7d)
  if (rate !== null) items.push(`Rate ${signed(rate)} lb / wk`)

  items.push(second)

  // Both or neither: they come from one read of one day, and a bar that showed
  // protein beside a missing calorie count would look like a bug in the number
  // that is there rather than an absence in the one that is not.
  const calories = finite(data.today?.calories)
  const protein = finite(data.today?.protein_g)
  if (calories !== null && protein !== null) {
    items.push(`Today ${grouped(calories)} kcal`)
    items.push(`Protein ${Math.round(protein)} g`)
  }

  items.push(third)
  return items
}

/* ── The reads ───────────────────────────────────────────────────────────── */

/**
 * Days of slack below this browser's date on the lower-bounded nutrition read.
 *
 * A floor on what is FETCHED, never a day boundary — `serverToday` decides which
 * day is today regardless. Two days covers any clock that disagrees with the
 * server's `MILO_TIMEZONE` by a day in either direction, and keeps the read to a
 * handful of rows. `screens/Macros.tsx` uses a wider slack because it also has to
 * *display* seven days; this only has to bracket one.
 */
const PIN_SLACK = 2

function toIso(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

function isAbort(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError'
}

/**
 * Load the bar's numbers. Renders the mottos immediately and fills in around
 * them as the reads land; a read that fails is simply never filled in.
 *
 * Nothing here reports an error, on purpose. A ticker that turned red would be a
 * second, worse copy of an error TRENDS and MACROS already show properly, on a
 * bar that is decorative by construction. Failing quietly is the design.
 *
 * @param enabled false while signed out, so a signed-out page load fires no
 *   requests and the ticker shows the mottos it always did.
 * @param refreshEpoch the shell sync's refresh signal (issue #88) — a bump
 *   means a FatSecret run finished after these reads went out, so they run
 *   again and the bar's numbers agree with the screens'. Defaults to 0, which
 *   is "never", so nothing changes for a caller that does not pass it.
 */
export function useTicker(enabled: boolean, refreshEpoch = 0): string[] {
  const [data, setData] = useState<TickerData>(NO_DATA)

  useEffect(() => {
    if (!enabled) {
      setData(NO_DATA)
      return
    }

    const controller = new AbortController()
    const { signal } = controller

    void (async () => {
      try {
        // Server default window (90 days). `macros_weekly` is not read here, and
        // `days` would not widen it anyway.
        const trends = await getTrends({}, signal)
        setData((current) => ({ ...current, weight: trends.weight }))
      } catch (error) {
        if (isAbort(error)) return
        // Leave `weight` null. The TRENDS tab says what went wrong.
      }
    })()

    void (async () => {
      try {
        /* Two reads, because between them they bracket the server's day — the
           same inequality `screens/Macros.tsx` documents at length:

             days: 0        → `date >= server today`, unbounded above.
             from, no to    → `date <= server today`.

           A date in both is the server's today. Neither read alone can name it:
           the first is polluted by future-dated rows, and the second cannot tell
           its last row from an older one. */
        const from = toIso(new Date(Date.now() - PIN_SLACK * 86_400_000))
        const [atOrAfterToday, throughToday] = await Promise.all([
          getNutrition({ days: 0 }, signal),
          getNutrition({ from }, signal),
        ])
        const byDate = groupByDate(throughToday.entries)
        const todayIso = serverToday(atOrAfterToday.entries, byDate)
        // Null means no row is dated the server's today. Not "assume zero" and
        // not "assume this browser is right" — the bar just does not mention it.
        const totals = todayIso === null ? null : byDate.get(todayIso) ?? null
        setData((current) => ({
          ...current,
          today: totals && { calories: totals.calories, protein_g: totals.protein_g },
        }))
      } catch (error) {
        if (isAbort(error)) return
        // Leave `today` null. The MACROS tab says what went wrong.
      }
    })()

    return () => controller.abort()
  }, [enabled, refreshEpoch])

  return useMemo(() => buildTicker(data), [data])
}
